// RemindersCard — list + create/edit/delete custom user reminders. Each row
// shows label, recurrence summary, next-fire time, enable toggle. Add button
// opens a modal with the RecurrencePicker; same modal serves edit by passing
// an initial reminder.

import { useEffect, useState } from 'react';
import { api, APIError } from '../lib/api';
import type { CreateUserReminder, UserReminder } from '../lib/api';
import { Card } from './Card';
import { Chip } from './Chip';
import { Modal } from './Modal';
import { RecurrencePicker, summarizeRecurrence } from './RecurrencePicker';
import type { RecurrenceState } from './RecurrencePicker';
import { TimePicker } from './TimePicker';

export function RemindersCard() {
  const [reminders, setReminders] = useState<UserReminder[] | null>(null);
  const [editing, setEditing] = useState<UserReminder | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const refresh = async () => {
    try {
      const r = await api.listUserReminders();
      setReminders(r.reminders);
    } catch (e) {
      setError(e instanceof APIError ? `Load: ${e.status}` : 'Load failed');
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const toggleEnabled = async (rem: UserReminder, enabled: boolean) => {
    setBusy(rem.id);
    try {
      await api.updateUserReminder(rem.id, toCreatePayload({ ...rem, enabled }));
      await refresh();
    } catch (e) {
      setError(e instanceof APIError ? `Update: ${e.status}` : 'Update failed');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (rem: UserReminder) => {
    if (!confirm(`Delete reminder "${rem.label}"?`)) return;
    setBusy(rem.id);
    try {
      await api.deleteUserReminder(rem.id);
      await refresh();
    } catch (e) {
      setError(e instanceof APIError ? `Delete: ${e.status}` : 'Delete failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Card
        title="Custom reminders"
        hint="Your own alarms. One-time or recurring."
        action={
          <button
            type="button"
            className="btn-primary px-3.5 py-2 text-xs font-mono uppercase tracking-[0.14em]"
            onClick={() => setEditing('new')}
          >
            + Add
          </button>
        }
      >
        {error && <p className="text-xs text-danger">{error}</p>}
        {!reminders && <p className="text-sm text-fg-muted">Loading…</p>}
        {reminders && reminders.length === 0 && (
          <p className="text-sm text-fg-muted">No reminders yet. Add one — it's like an iPhone alarm.</p>
        )}
        {reminders && reminders.length > 0 && (
          <div className="space-y-1">
            {reminders.map((r) => (
              <ReminderRow
                key={r.id}
                rem={r}
                busy={busy === r.id}
                onToggle={(en) => toggleEnabled(r, en)}
                onEdit={() => setEditing(r)}
                onDelete={() => remove(r)}
              />
            ))}
          </div>
        )}
      </Card>
      {editing && (
        <ReminderModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </>
  );
}

function ReminderRow({
  rem,
  busy,
  onToggle,
  onEdit,
  onDelete,
}: {
  rem: UserReminder;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const summary = summarizeRecurrence(toRecurrenceState(rem), rem.hour, rem.minute);
  return (
    <div className="flex items-start gap-3 p-2.5 rounded-panel transition-colors hover:bg-fg/[0.04]">
      <button
        type="button"
        onClick={() => onToggle(!rem.enabled)}
        disabled={busy}
        aria-label={rem.enabled ? 'Disable' : 'Enable'}
        className={`mt-0.5 grid place-items-center size-5 rounded-[7px] border transition-all shrink-0 ${
          rem.enabled
            ? 'bg-accent border-accent text-accent-fg'
            : 'bg-bg-sunken border-line-strong text-transparent'
        }`}
      >
        <svg viewBox="0 0 16 16" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8.5l3.5 3.5L13 5" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onEdit}
        className="flex-1 min-w-0 text-left"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className={`text-sm ${rem.enabled ? 'text-fg' : 'text-fg-faint'}`}>{rem.label}</span>
          {rem.next_fire_at && rem.enabled && (
            <Chip tone="muted">{formatNextFire(rem.next_fire_at)}</Chip>
          )}
        </div>
        <p className="text-xs text-fg-faint mt-0.5">{summary}</p>
        {rem.body && <p className="text-xs text-fg-muted mt-0.5 line-clamp-2">{rem.body}</p>}
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        className="text-fg-faint hover:text-danger text-sm leading-none px-1 transition-colors"
        aria-label="Delete"
      >
        ✕
      </button>
    </div>
  );
}

function ReminderModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: UserReminder | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [label, setLabel] = useState(initial?.label ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [hour, setHour] = useState(initial?.hour ?? 9);
  const [minute, setMinute] = useState(initial?.minute ?? 0);
  const [recurrence, setRecurrence] = useState<RecurrenceState>(
    initial ? toRecurrenceState(initial) : {
      freq: 'ONCE',
      intervalN: 1,
      startDate: today,
      endType: 'never',
    },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!label.trim()) {
      setError('Label is required');
      return;
    }
    setBusy(true);
    setError(null);
    const payload: CreateUserReminder = {
      label: label.trim(),
      body: body.trim(),
      hour,
      minute,
      freq: recurrence.freq,
      interval_n: recurrence.intervalN,
      days_mask: recurrence.daysMask,
      monthly_day: recurrence.monthlyDay,
      start_date: recurrence.startDate,
      end_type: recurrence.endType,
      end_date: recurrence.endDate,
      end_count: recurrence.endCount,
      enabled: initial?.enabled ?? true,
    };
    try {
      if (initial) {
        await api.updateUserReminder(initial.id, payload);
      } else {
        await api.createUserReminder(payload);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof APIError && typeof e.body === 'object' && e.body && 'error' in e.body
        ? String((e.body as { error: string }).error)
        : 'Save failed');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={initial ? 'Edit reminder' : 'New reminder'}
      open
      onClose={onClose}
      footer={
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="btn-primary w-full py-3 text-xs font-mono uppercase tracking-[0.14em]"
        >
          {busy ? 'Saving…' : initial ? 'Save changes' : 'Create reminder'}
        </button>
      }
    >
      <label className="block space-y-1.5">
        <span className="label-eyebrow">Label</span>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={200}
          placeholder="e.g. Drink water"
          className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm text-fg placeholder:text-fg-faint transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
        />
      </label>
      <label className="block space-y-1.5">
        <span className="label-eyebrow">Body (optional)</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={500}
          rows={2}
          placeholder="Extra detail shown in the notification"
          className="w-full rounded-control bg-bg-sunken border border-line px-3 py-2.5 text-sm text-fg placeholder:text-fg-faint transition-colors resize-none focus:outline-none focus:border-accent focus:bg-bg-raised"
        />
      </label>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">Time</span>
        <TimePicker hour={hour} minute={minute} onChange={(h, m) => { setHour(h); setMinute(m); }} />
      </div>
      <RecurrencePicker value={recurrence} onChange={setRecurrence} />
      {error && <p className="text-xs text-danger">{error}</p>}
    </Modal>
  );
}

function toRecurrenceState(r: UserReminder): RecurrenceState {
  return {
    freq: r.freq,
    intervalN: r.interval_n,
    daysMask: r.days_mask,
    monthlyDay: r.monthly_day,
    startDate: r.start_date,
    endType: r.end_type,
    endDate: r.end_date,
    endCount: r.end_count,
  };
}

function toCreatePayload(r: UserReminder): CreateUserReminder {
  return {
    label: r.label,
    body: r.body,
    hour: r.hour,
    minute: r.minute,
    freq: r.freq,
    interval_n: r.interval_n,
    days_mask: r.days_mask,
    monthly_day: r.monthly_day,
    start_date: r.start_date,
    end_type: r.end_type,
    end_date: r.end_date,
    end_count: r.end_count,
    enabled: r.enabled,
  };
}

function formatNextFire(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  if (sameDay) return `Today ${time}`;
  if (isTomorrow) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}
